import { Box3, Euler, Vector3 } from 'three'
import {
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoHoodBend,
  GeckoHoodStraight,
  GeckoMeasurementUnit,
  GeckoPipeElbow,
  SceneConfig,
} from 'types'

/**
 * Returns bounding box coordinates in XYZ space for the component model, or undefined if the
 * respective scene config isn't present.
 * Note: .getCenter() and .getSize() can be used to get the center and size of the box.
 * @param components
 * @param show3D - determines which scene config to use
 * @returns
 */
export const getComponentBoundingBox3D = (
  componentModel: GeckoComponentModel,
  show3D: boolean
): Box3 | undefined => {
  const boundingCoords: Vector3[] = []
  const { position, rotation } = getComponentPositionAndRotations(
    componentModel,
    show3D
  )
  if (!position || !rotation) return undefined
  const componentBoundingCoords = getBoundingCoordinatesForComponent(
    componentModel,
    show3D
  )
  componentBoundingCoords.forEach((coordinate) => {
    applyPositionAndRotation(coordinate, position, rotation)
    boundingCoords.push(coordinate)
  })
  return getBoundingBoxFromCoordinates(boundingCoords)
}

/**
 * Returns coordinates in XYZ space for the min, max, and center for either
 * the 2D or 3D view.
 * of the bounding box of all of components. Ignores components with an
 * undefined respective scene config.
 * @param components
 * @param show3D - determines which scene config to use
 * @returns
 */
export const getBoundingBoxOfAssetModel = (
  components: Map<string, GeckoComponentModel>,
  show3D: boolean
): Box3 => {
  const boundingCoords: Vector3[] = []
  for (const component of components.values()) {
    const { position, rotation } = getComponentPositionAndRotations(
      component,
      show3D
    )
    if (!position || !rotation) continue
    const componentBoundingCoords = getBoundingCoordinatesForComponent(
      component,
      show3D
    )
    componentBoundingCoords.forEach((coordinate) => {
      applyPositionAndRotation(coordinate, position, rotation)
      boundingCoords.push(coordinate)
    })
  }

  return getBoundingBoxFromCoordinates(boundingCoords)
}

/**
 * Get the bounding box from the given coordinates.
 */
const getBoundingBoxFromCoordinates = (coordinates: Vector3[]): Box3 => {
  const max = new Vector3(-Infinity, -Infinity, -Infinity)
  const min = new Vector3(Infinity, Infinity, Infinity)
  for (const coordinate of coordinates) {
    max.max(coordinate)
    min.min(coordinate)
  }
  max.max(min)
  min.min(max)
  return new Box3(min, max)
}

/**
 *  Returns the component rotation and position for either
 * 2D or 3D mode, if defined. Otherwise returns undefined for both
 * if the respective scene config is undefined.
 * @returns
 */
export const getComponentPositionAndRotations = (
  component: GeckoComponentModel,
  show3D: boolean
):
  | { position: Readonly<Vector3>; rotation: Readonly<Euler> }
  | { position: undefined; rotation: undefined } => {
  // Check for sceneConfig first, then fallback to JSON scene_config
  const sceneConfig =
    component.sceneConfig ?? (component.json?.scene_config as SceneConfig)

  if (show3D) {
    if (!sceneConfig?.config_3d)
      return { position: undefined, rotation: undefined }
    const position = new Vector3(...sceneConfig.config_3d.position)
    const rotation = new Euler(
      (sceneConfig.config_3d.rotation[0] * Math.PI) / 180,
      (sceneConfig.config_3d.rotation[1] * Math.PI) / 180,
      (sceneConfig.config_3d.rotation[2] * Math.PI) / 180
    )
    return { position, rotation }
  } else {
    if (!sceneConfig?.config_2d)
      return { position: undefined, rotation: undefined }
    const position = new Vector3(...sceneConfig.config_2d.position, 0)
    const rotation = new Euler(
      0,
      0,
      (sceneConfig.config_2d.rotation * Math.PI) / 180
    )
    return { position, rotation }
  }
}

/** Returns the 4 or 8 bounding coordinates of a component, based on component type,
 * in XYZ space.
 */
const getBoundingCoordinatesForComponent = (
  component: GeckoComponentModel,
  show3D: boolean
): Vector3[] => {
  if (
    !show3D &&
    component.type !== GeckoComponentModelType.Cone &&
    component.type !== GeckoComponentModelType.TankFloor &&
    component.type !== GeckoComponentModelType.TankFlatRoof
  ) {
    return getPlanarBoundingCoordinates(
      component.componentBounds.x.delta.toNumber(GeckoMeasurementUnit.Meters),
      component.componentBounds.y.delta.toNumber(GeckoMeasurementUnit.Meters)
    )
  }
  if (component.type === GeckoComponentModelType.Cone)
    return show3D
      ? getCylindricalBoundingCoordinates(
          component.outerRadius.toNumber(GeckoMeasurementUnit.Meters),
          component.coneHeight.toNumber(GeckoMeasurementUnit.Meters)
        )
      : getCircularBoundingCoordinates(
          component.outerRadius.toNumber(GeckoMeasurementUnit.Meters),
          true
        )

  return component.type === GeckoComponentModelType.VerticalTankShell
    ? getCylindricalBoundingCoordinates(
        component.radius.toNumber(GeckoMeasurementUnit.Meters),
        component.height.toNumber(GeckoMeasurementUnit.Meters)
      )
    : component.type === GeckoComponentModelType.PressureVesselShell
      ? getCylindricalBoundingCoordinates(
          component.radius.toNumber(GeckoMeasurementUnit.Meters),
          component.length.toNumber(GeckoMeasurementUnit.Meters)
        )
      : component.type === GeckoComponentModelType.HemisphericalHead
        ? show3D
          ? getCylindricalBoundingCoordinates(
              component.radius.toNumber(GeckoMeasurementUnit.Meters),
              component.radius.toNumber(GeckoMeasurementUnit.Meters)
            )
          : getCircularBoundingCoordinates(
              component.radius.toNumber(GeckoMeasurementUnit.Meters),
              true
            )
        : component.type === GeckoComponentModelType.TorisphericalHead
          ? show3D
            ? getCylindricalBoundingCoordinates(
                component.crownRadius.toNumber(GeckoMeasurementUnit.Meters),
                component.crownRadius.toNumber(GeckoMeasurementUnit.Meters)
              )
            : getCircularBoundingCoordinates(
                component.crownRadius.toNumber(GeckoMeasurementUnit.Meters),
                true
              )
          : component.type === GeckoComponentModelType.EllipsoidalHead
            ? show3D
              ? getCylindricalBoundingCoordinates(
                  component.radius.toNumber(GeckoMeasurementUnit.Meters),
                  component.minorRadius.toNumber(GeckoMeasurementUnit.Meters)
                )
              : getCircularBoundingCoordinates(
                  component.radius.toNumber(GeckoMeasurementUnit.Meters),
                  false // Use false for elliptical heads in 2D
                )
            : component.type === GeckoComponentModelType.PipeSection
              ? getCylindricalBoundingCoordinates(
                  component.radius.toNumber(GeckoMeasurementUnit.Meters),
                  component.length.toNumber(GeckoMeasurementUnit.Meters)
                )
              : component instanceof GeckoHoodStraight
                ? getCylindricalBoundingCoordinates(
                    component.radius.toNumber(GeckoMeasurementUnit.Meters),
                    component.length.toNumber(GeckoMeasurementUnit.Meters)
                  )
                : component instanceof GeckoHoodBend ||
                    component instanceof GeckoPipeElbow
                  ? getCylindricalBoundingCoordinates(
                      component.majorRadius.toNumber(
                        GeckoMeasurementUnit.Meters
                      ),
                      component.majorRadius.toNumber(
                        GeckoMeasurementUnit.Meters
                      )
                    )
                  : component.type === GeckoComponentModelType.PipeTJoint
                    ? getCylindricalBoundingCoordinates(
                        component.componentBounds.x.delta.toNumber(
                          GeckoMeasurementUnit.Meters
                        ),
                        component.componentBounds.y.delta.toNumber(
                          GeckoMeasurementUnit.Meters
                        )
                      )
                    : component.type ===
                        GeckoComponentModelType.PipeEccentricReducer
                      ? getCylindricalBoundingCoordinates(
                          component.length.toNumber(
                            GeckoMeasurementUnit.Meters
                          ),
                          component.inlet_radius.toNumber(
                            GeckoMeasurementUnit.Meters
                          )
                        )
                      : component.type === GeckoComponentModelType.TankFloor ||
                          component.type ===
                            GeckoComponentModelType.TankFlatRoof ||
                          component.type ===
                            GeckoComponentModelType.FlatCircularFloor
                        ? getCircularBoundingCoordinates(
                            component.radius.toNumber(
                              GeckoMeasurementUnit.Meters
                            ),
                            !show3D
                          )
                        : []
}

/**
 * Applys the rotation and position shift directly to the coordinate.
 */
const applyPositionAndRotation = (
  coordinate: Vector3,
  position: Vector3,
  rotation: Euler
): void => {
  coordinate.applyEuler(rotation)
  coordinate.add(position)
}

const getCylindricalBoundingCoordinates = (
  radius: number,
  height: number
): Vector3[] => {
  return [
    new Vector3(-radius, 0, -radius),
    new Vector3(-radius, 0, radius),
    new Vector3(radius, 0, -radius),
    new Vector3(radius, 0, radius),
    new Vector3(-radius, height, -radius),
    new Vector3(-radius, height, radius),
    new Vector3(radius, height, -radius),
    new Vector3(radius, height, radius),
  ]
}

const getCircularBoundingCoordinates = (
  radius: number,
  in2D: boolean = false
): Vector3[] => {
  return in2D
    ? [
        new Vector3(-radius, -radius, 0),
        new Vector3(-radius, radius, 0),
        new Vector3(radius, -radius, 0),
        new Vector3(radius, radius, 0),
      ]
    : [
        new Vector3(-radius, 0, -radius),
        new Vector3(-radius, 0, radius),
        new Vector3(radius, 0, -radius),
        new Vector3(radius, 0, radius),
      ]
}

const getPlanarBoundingCoordinates = (
  width: number,
  height: number
): Vector3[] => {
  return [
    new Vector3(0, 0, 0),
    new Vector3(width, width, 0),
    new Vector3(0, height, 0),
    new Vector3(width, height, 0),
  ]
}
